/**
 * ErrorBoundary Component
 * 
 * Catches React errors in child component tree and displays fallback UI
 */

import React from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle } from 'lucide-react';
import { logger } from '../utils/logger';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
    
    // Log error to console (always)
    console.error('[ErrorBoundary] Caught error:', error?.message || String(error), error?.stack);
    console.error('[ErrorBoundary] Component stack:', errorInfo?.componentStack);
    
    // Production guard: Do not attempt localhost/HTTP calls in production
    // Error logging to external services should use HTTPS endpoints if needed
    
    // Log error to logger in development
    if (import.meta.env.MODE === 'development') {
      logger.error('ErrorBoundary caught an error', {
        error: error?.message || String(error),
        stack: error?.stack,
        componentStack: errorInfo?.componentStack,
      }, ['react', 'error-boundary']);
    }
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="flex flex-col items-center justify-center p-8 bg-red-50 border-2 border-red-200 rounded-lg">
          <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {this.props.errorTitle || 'Something went wrong'}
          </h3>
          <p className="text-sm text-gray-600 text-center max-w-md mb-4">
            {this.props.errorMessage || 'An error occurred while rendering this component. Please try refreshing the page.'}
          </p>
          {import.meta.env.MODE === 'development' && this.state.error && (
            <details className="mt-4 p-4 bg-white rounded border border-red-300 text-left max-w-2xl w-full">
              <summary className="cursor-pointer font-medium text-red-700 mb-2">
                Error Details (Development Only)
              </summary>
              <pre className="text-xs text-gray-700 overflow-auto">
                {this.state.error.toString()}
                {'\n\n'}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          )}
          {this.props.onReset && (
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null, errorInfo: null });
                this.props.onReset();
              }}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  errorTitle: PropTypes.string,
  errorMessage: PropTypes.string,
  onReset: PropTypes.func,
};

export default ErrorBoundary;
